import { useState, useRef, useCallback, useEffect } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";

interface ExtractorStatus {
    task_id?: string;
    status: "processing" | "completed" | "failed" | "error" | "PENDING" | "STARTED" | "SUCCESS" | "FAILURE";
    state?: string;
    ready?: boolean;
    successful?: boolean;
    failed?: boolean;
    result?: Record<string, unknown>;
    error?: string;
    message?: string;
}

interface UseExtractorSSEOptions {
    onStatusChange?: (status: ExtractorStatus) => void;
    onComplete?: (result: Record<string, unknown>) => void;
    onError?: (error: string) => void;
}

export function useExtractorSSE(options: UseExtractorSSEOptions = {}) {
    const [isConnected, setIsConnected] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<ExtractorStatus | null>(null);
    const eventSourceRef = useRef<EventSourcePolyfill | null>(null);
    const isConnectingRef = useRef(false);

    const connect = useCallback((taskId: string) => {
        if (!taskId || isConnectingRef.current || eventSourceRef.current) {
            return;
        }

        const token = localStorage.getItem("access_token");
        if (!token) {
            return;
        }

        const url = `${import.meta.env.VITE_API_URL}/api/v1/extractor/result/${taskId}/stream`;

        const es = new EventSourcePolyfill(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
            heartbeatTimeout: 300000, // 5분
        });

        isConnectingRef.current = true;

        es.onopen = () => {
            setIsConnected(true);
            isConnectingRef.current = false;
        };

        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                setCurrentStatus(data);
                options.onStatusChange?.(data);

                // 다양한 완료 상태 처리
                const isCompleted = data.status === "completed" ||
                    data.status === "SUCCESS" ||
                    (data.ready && data.successful);

                const isFailed = data.status === "failed" ||
                    data.status === "FAILURE" ||
                    data.status === "error" ||
                    (data.ready && data.failed);

                if (isCompleted && data.result) {
                    options.onComplete?.(data.result);
                    disconnect();
                } else if (isFailed) {
                    const errorMessage = data.error || data.message || "알 수 없는 오류";
                    options.onError?.(errorMessage);
                    disconnect();
                }
            } catch (error) {
            }
        };

        es.onerror = () => {
            setIsConnected(false);
            isConnectingRef.current = false;
            disconnect();
        };

        eventSourceRef.current = es;
    }, [options]);

    const disconnect = useCallback(() => {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
            isConnectingRef.current = false;
            setIsConnected(false);
        }
    }, []);

    // 컴포넌트 언마운트 시 연결 해제
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    return {
        isConnected,
        currentStatus,
        connect,
        disconnect,
    };
} 